import { computed, defineComponent, onBeforeUnmount, onMounted, ref, } from 'vue';
export default defineComponent({
    name: 'RotatingCards',
    props: {
        items: {
            type: Array,
            required: true,
        },
        initialIndex: {
            type: Number,
            default: 0,
        },
        prevLabel: {
            type: String,
            default: 'Previous',
        },
        nextLabel: {
            type: String,
            default: 'Next',
        },
        autoplay: {
            type: Boolean,
            default: true,
        },
        autoplayInterval: {
            type: Number,
            default: 4200,
        },
        extendRight: {
            type: Number,
            default: 0,
        },
    },
    setup(props) {
        const root = ref(null);
        const activeIndex = ref(props.initialIndex);
        const paused = ref(false);
        const wrap = (index) => {
            const len = props.items.length;
            return ((index % len) + len) % len;
        };
        const next = () => { activeIndex.value = wrap(activeIndex.value + 1); };
        const prev = () => { activeIndex.value = wrap(activeIndex.value - 1); };
        const goTo = (offset) => {
            if (offset === 0)
                return;
            activeIndex.value = wrap(activeIndex.value + offset);
        };
        const visibleCards = computed(() => {
            const len = props.items.length;
            const baseSpan = Math.min(2, Math.floor((len - 1) / 2));
            const leftSpan = baseSpan;
            const rightSpan = Math.min(2, baseSpan + props.extendRight);
            // Cap total visible to len so no item appears twice
            const totalSpan = Math.min(leftSpan + rightSpan + 1, len);
            const trimmedRight = Math.min(rightSpan, totalSpan - leftSpan - 1);
            const result = [];
            for (let offset = -leftSpan; offset <= trimmedRight; offset += 1) {
                const idx = wrap(activeIndex.value + offset);
                result.push({ item: props.items[idx], offset, itemIndex: idx });
            }
            return result;
        });
        let autoplayTimer = null;
        let observer = null;
        let prefersReducedMotion = false;
        const stopAutoplay = () => {
            if (autoplayTimer !== null) {
                window.clearInterval(autoplayTimer);
                autoplayTimer = null;
            }
        };
        const startAutoplay = () => {
            if (!props.autoplay || prefersReducedMotion || paused.value)
                return;
            stopAutoplay();
            autoplayTimer = window.setInterval(next, props.autoplayInterval);
        };
        const onPause = () => { paused.value = true; stopAutoplay(); };
        const onResume = () => { paused.value = false; startAutoplay(); };
        const onUserNav = (action) => {
            action();
            // restart timer so user gets full interval after manual nav
            if (props.autoplay && !prefersReducedMotion && !paused.value)
                startAutoplay();
        };
        const onKeydown = (e) => {
            if (!root.value || !root.value.contains(document.activeElement))
                return;
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                onUserNav(next);
            }
            else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                onUserNav(prev);
            }
        };
        onMounted(() => {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            prefersReducedMotion = mq.matches;
            mq.addEventListener('change', (e) => { prefersReducedMotion = e.matches; });
            window.addEventListener('keydown', onKeydown);
            if (root.value) {
                observer = new IntersectionObserver(([entry]) => {
                    if (entry.isIntersecting) {
                        startAutoplay();
                        observer?.disconnect();
                    }
                }, { threshold: 0.25 });
                observer.observe(root.value);
            }
        });
        onBeforeUnmount(() => {
            window.removeEventListener('keydown', onKeydown);
            stopAutoplay();
            observer?.disconnect();
        });
        return {
            root,
            activeIndex,
            visibleCards,
            next: () => onUserNav(next),
            prev: () => onUserNav(prev),
            goTo: (offset) => onUserNav(() => goTo(offset)),
            onPause,
            onResume,
        };
    },
});
